// Importing from @datadog package to avoid circular dependency with @disco/utils/datadog
import { datadogRum } from '@datadog/browser-rum'
import type { HttpErrorType } from 'types/errors'

export class HttpError extends Error {
  status: number = 0
  body: Record<string, any> = {}
  code: string | null | undefined = null

  constructor(message: string, status: number, body?: Record<string, any>) {
    super()
    this.message = message
    this.status = status
    this.body = body || {}
  }
}

// don't bother logging endpoints where 403 error handled safely
const safe403 = [
  'api.getUserDetails',
  'api.getCurrentBusiness',
  'api.getPlaylistTypes',
  'api.getPane',
  'api.getWebsocketParams',
  'api.getFullPlaylist',
]
const statusLevels = {
  '503': 'info',
  '504': 'info',
  '404': 'info',
  '403': 'info',
  '500': 'info',
}
const statusTextMapping = {
  '503': 'Service unavailable',
  '504': 'Gateway timeout',
  '500': 'Internal server error',
  '403': 'Authentication required',
}

const skipErrorLogging = (error: HttpErrorType, source: string) => {
  if (error.status === 403 && safe403.indexOf(source) > -1) return true
  // don't log errors with empty statuses and without body,
  // those are cancelled requests and are useless
  if (!error.status && !error.body) return true
  return false
}

// default error handler, captures error on api level and rejects promise
function errorHandler(
  error: HttpErrorType,
  source: string,
  reject?: (error?: any) => void,
): void {
  const errorObj = new HttpError(error.message, error.status)

  if (error.body) {
    errorObj.code = error.body.code
    errorObj.body = error.body
  }

  if (!skipErrorLogging(error, source)) {
    const level = statusLevels[error.status] || 'error'
    const apiMessage = statusTextMapping[error.status] || source
    datadogRum.addError(errorObj, {
      description: 'API error',
      extra: { apiMessage, source, error, level },
    })
  }

  if (reject) {
    reject(errorObj)
  }
}

export default errorHandler
